import { useCallback, useContext, useState } from 'react';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';

import AccountPickerContext from 'contexts/AccountPickerContext';
import AnalyticsContext from 'contexts/AnalyticsContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import DashboardsContext from 'contexts/DashboardsContext';
import ToastContext from 'contexts/ToastContext';
import { PortalsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import { buildDashboardShow, buildReportShow } from 'navigation/appRoutes';
import buildCardLayout from 'contextProviders/WidgetGalleryProvider/buildCardLayout';
import findStartingY from 'contextProviders/WidgetGalleryProvider/findStartingY';
import createDashboardAlt from 'api/createDashboardAlt';
import createDashboardGadget from 'api/createDashboardGadget';
import updateDashboard from 'api/updateDashboard';
import createMetric from 'api/metrics/createMetric';
import createCompoundMetric from 'api/compoundMetrics/createCompoundMetric';
import withoutNulls from 'api/expression/withoutNulls';
import appNavItemTypeCheckers from 'types/appNavItemTypeCheckers';
import getIdentifier from 'getIdentifier';
import STORE from 'store';
import {
  InsightsChartSpec,
  ProposedAction,
} from 'services/streamInsightsQuery';

import buildChartDefFromSpec, { canSaveSpec } from './buildChartDefFromSpec';
import buildScopeFromSource from './buildScopeFromSource';

// A resolved add-to-existing destination — the dashboard/report the charts get
// appended to (chosen from the active entity or the picker). Discriminated so no
// cast is needed when dispatching.
export type AddTarget =
  | { kind: 'dashboard'; entity: DashboardType }
  | { kind: 'report'; entity: ReportType };

// Phase 3c — what add_to_workspace places into the chosen workspace: either a
// brand-new dashboard built from the charts (named by `title`), or an existing
// dashboard/report.
export type WorkspaceContentChoice =
  | { kind: 'new'; title: string }
  | { kind: 'existing'; item: AddTarget };

// A resolved add_to_workspace destination: the V3 workspace plus the content
// choice. `kind: 'workspace'` keeps it distinct from AddTarget in the union.
export type WorkspaceTarget = {
  kind: 'workspace';
  workspace: V3.Workspace;
  content: WorkspaceContentChoice;
};

// The destination the confirm card resolves for an add-style proposal (3b/3c).
export type ConfirmTarget = AddTarget | WorkspaceTarget;

// A metric made from the agent's proposal, as the agent is later told of it
export interface CreatedMetric {
  metricId: string;
  metricName: string;
}

// Cards on a fresh canvas: a simple 2-up grid (each half-width, 7 rows tall).
const CARDS_PER_ROW = 2;
const CARD_WIDTH = 6;
const CARD_HEIGHT = 7;

// Commits a Phase 3 build proposal: creates a NEW dashboard or report holding a
// live gadget per proposed chart, then toasts + navigates to it. Mirrors
// useSaveInsightsChart's new-dashboard/new-report paths, generalised to N
// charts. Each gadget rebuilds a fresh V5ChartDefinition from the chart's
// `source` (re-queries data) and carries the chat's filters as its scope.
const useCreateInsightsContent = () => {
  const { selectedAccountId, accountRef } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { addOrUpdateDashboard } = useContext(DashboardsContext);
  const { selectedPortal } = useContext(PortalsContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();

  const [isSaving, setIsSaving] = useState(false);

  // A fresh gadget (dashboard- or report-shaped) for one proposed chart. Flagged
  // isAIGenerated so the UI can mark AI-origin gadgets.
  const buildGadget = useCallback(
    (spec: InsightsChartSpec, now: string): DashboardGadget => ({
      id: getIdentifier(),
      chartDef: buildChartDefFromSpec(spec),
      drillDowns: [],
      scope: buildScopeFromSource(spec.source.filters),
      dateField: 'date',
      dateRange: {
        startDate: spec.source.dateScope.startDate,
        endDate: spec.source.dateScope.endDate,
      },
      isAIGenerated: true,
      createdBy: currentUserId,
      createdOn: now,
      updatedBy: currentUserId,
      updatedOn: now,
    }),
    [currentUserId],
  );

  // Lay card `index` into a 2-up grid. `baseY` shifts the grid down so
  // appended cards land below an existing canvas (0 for a fresh canvas).
  const layoutFor = useCallback(
    (index: number, baseY = 0) =>
      buildCardLayout(
        (index % CARDS_PER_ROW) * CARD_WIDTH,
        baseY + Math.floor(index / CARDS_PER_ROW) * CARD_HEIGHT,
        undefined,
        CARD_WIDTH,
        CARD_HEIGHT,
      ),
    [],
  );

  const finish = useCallback(
    ({
      kind,
      name,
      route,
      message,
    }: {
      kind: string;
      name: string;
      route: string;
      // Override the default "Created {kind} …" toast (e.g. for add-to-workspace).
      message?: string;
    }) => {
      setIsSaving(false);
      showToast(message ?? `Created ${kind} “${name}”`);
      trackEvent('Insights - Content Built', { kind });
      navigate(route);
      return true;
    },
    [navigate, showToast, trackEvent],
  );

  // Build + persist a NEW dashboard holding a live gadget per chart, register it
  // locally, and return it — WITHOUT toasting/navigating. Shared by the create-
  // dashboard flow and the add-to-workspace wrap-in-a-new-dashboard path.
  const persistNewDashboard = useCallback(
    async ({
      title,
      charts,
    }: {
      title: string;
      charts: InsightsChartSpec[];
    }): Promise<DashboardType> => {
      const now = DateTime.utc().toISO();
      const cards: CanvasCard.DashboardGadgetCard[] = [];
      for (let i = 0; i < charts.length; i += 1) {
        const gadget = buildGadget(charts[i], now);
        // eslint-disable-next-line no-await-in-loop
        await createDashboardGadget(gadget, selectedAccountId);
        cards.push({
          layout: layoutFor(i),
          content: { type: 'Dashboard Gadget', dashboardGadgetId: gadget.id },
        });
      }
      const newDashboard: DashboardType = {
        version: '5',
        id: getIdentifier(),
        name: title,
        canvas: { cards },
        isTemplate: false,
        variableDrillDowns: [],
        // Default to private; the user re-shares on the created dashboard.
        access: { version: '2', type: 'Private' },
        isV3Published: false,
        v3Access: { isPublic: false, groupIds: [], userIds: [] },
        labelIds: [],
        isAIGenerated: true,
        createdBy: currentUserId,
        createdOn: now,
        updatedBy: currentUserId,
        updatedOn: now,
      };
      await createDashboardAlt({ dashboard: newDashboard, accountRef });
      addOrUpdateDashboard(newDashboard);
      return newDashboard;
    },
    [
      buildGadget,
      layoutFor,
      selectedAccountId,
      currentUserId,
      accountRef,
      addOrUpdateDashboard,
    ],
  );

  const createDashboard = useCallback(
    async ({
      title,
      charts,
    }: {
      title: string;
      charts: InsightsChartSpec[];
    }) => {
      const newDashboard = await persistNewDashboard({ title, charts });
      return finish({
        kind: 'dashboard',
        name: title,
        route: buildDashboardShow({
          id: newDashboard.id,
          isEditing: true,
          portal: selectedPortal,
        }),
      });
    },
    [persistNewDashboard, finish, selectedPortal],
  );

  const createReport = useCallback(
    async ({
      title,
      charts,
    }: {
      title: string;
      charts: InsightsChartSpec[];
    }) => {
      const now = DateTime.utc().toISO();
      const cards: CanvasCard.ReportGadgetCard[] = [];
      for (let i = 0; i < charts.length; i += 1) {
        const gadget = buildGadget(charts[i], now);
        const reportGadget: ReportGadgets.ReportGadget = {
          id: gadget.id,
          chartDef: gadget.chartDef,
          scope: gadget.scope,
          dateField: 'date',
          dateRange: gadget.dateRange,
          isAIGenerated: true,
          createdBy: currentUserId,
          createdOn: now,
          updatedBy: currentUserId,
          updatedOn: now,
        };
        // eslint-disable-next-line no-await-in-loop
        await STORE.visualisations
          .getReportGadgetsRef({ accountId: selectedAccountId })
          .doc(reportGadget.id)
          .set(reportGadget);
        cards.push({
          layout: layoutFor(i),
          content: { type: 'Report Gadget', reportGadgetId: reportGadget.id },
        });
      }
      const newReport: ReportType = {
        version: '7',
        id: getIdentifier(),
        name: title,
        scope: [],
        drillDowns: [],
        canvas: { cards },
        dateField: 'date',
        access: { version: '2', type: 'Private' },
        isV3Published: false,
        v3Access: { isPublic: false, groupIds: [], userIds: [] },
        labelIds: [],
        isAIGenerated: true,
        createdBy: currentUserId,
        createdOn: now,
        updatedBy: currentUserId,
        updatedOn: now,
      };
      await STORE.getReportsRef({ accountId: selectedAccountId })
        .doc(newReport.id)
        .set(withoutNulls(newReport));
      return finish({
        kind: 'report',
        name: title,
        route: buildReportShow({
          id: newReport.id,
          isEditing: true,
          portal: selectedPortal,
        }),
      });
    },
    [
      buildGadget,
      layoutFor,
      selectedAccountId,
      currentUserId,
      finish,
      selectedPortal,
    ],
  );

  // Append one gadget per chart to an existing dashboard, below its current
  // canvas, then navigate to it. Generalises useSaveInsightsChart's
  // saveToExistingDashboard to N charts.
  const addToExistingDashboard = useCallback(
    async ({
      dashboard,
      charts,
    }: {
      dashboard: DashboardType;
      charts: InsightsChartSpec[];
    }) => {
      const now = DateTime.utc().toISO();
      const baseY = findStartingY(dashboard.canvas.cards, 'desktop');
      const newCards: CanvasCard.DashboardGadgetCard[] = [];
      for (let i = 0; i < charts.length; i += 1) {
        const gadget = buildGadget(charts[i], now);
        // eslint-disable-next-line no-await-in-loop
        await createDashboardGadget(gadget, selectedAccountId);
        newCards.push({
          layout: layoutFor(i, baseY),
          content: { type: 'Dashboard Gadget', dashboardGadgetId: gadget.id },
        });
      }
      const newDashboard: DashboardType = {
        ...dashboard,
        canvas: {
          ...dashboard.canvas,
          cards: [...dashboard.canvas.cards, ...newCards],
        },
        updatedOn: now,
        updatedBy: currentUserId,
      };
      await updateDashboard({ id: dashboard.id, newDashboard, accountRef });
      addOrUpdateDashboard(newDashboard);
      return finish({
        kind: 'dashboard',
        name: dashboard.name,
        route: buildDashboardShow({
          id: dashboard.id,
          isEditing: true,
          portal: selectedPortal,
        }),
      });
    },
    [
      buildGadget,
      layoutFor,
      selectedAccountId,
      currentUserId,
      accountRef,
      addOrUpdateDashboard,
      finish,
      selectedPortal,
    ],
  );

  // Append one report gadget per chart to an existing report, below its current
  // canvas, then navigate to it.
  const addToExistingReport = useCallback(
    async ({
      report,
      charts,
    }: {
      report: ReportType;
      charts: InsightsChartSpec[];
    }) => {
      const now = DateTime.utc().toISO();
      const baseY = findStartingY(report.canvas.cards, 'desktop');
      const newCards: CanvasCard.ReportGadgetCard[] = [];
      for (let i = 0; i < charts.length; i += 1) {
        const gadget = buildGadget(charts[i], now);
        const reportGadget: ReportGadgets.ReportGadget = {
          id: gadget.id,
          chartDef: gadget.chartDef,
          scope: gadget.scope,
          dateField: 'date',
          dateRange: gadget.dateRange,
          isAIGenerated: true,
          createdBy: currentUserId,
          createdOn: now,
          updatedBy: currentUserId,
          updatedOn: now,
        };
        // eslint-disable-next-line no-await-in-loop
        await STORE.visualisations
          .getReportGadgetsRef({ accountId: selectedAccountId })
          .doc(reportGadget.id)
          .set(reportGadget);
        newCards.push({
          layout: layoutFor(i, baseY),
          content: { type: 'Report Gadget', reportGadgetId: reportGadget.id },
        });
      }
      const newReport: ReportType = {
        ...report,
        canvas: {
          ...report.canvas,
          cards: [...report.canvas.cards, ...newCards],
        },
        updatedOn: now,
        updatedBy: currentUserId,
      };
      await STORE.getReportsRef({ accountId: selectedAccountId })
        .doc(report.id)
        .set(withoutNulls(newReport));
      return finish({
        kind: 'report',
        name: report.name,
        route: buildReportShow({
          id: report.id,
          isEditing: true,
          portal: selectedPortal,
        }),
      });
    },
    [
      buildGadget,
      layoutFor,
      selectedAccountId,
      currentUserId,
      finish,
      selectedPortal,
    ],
  );

  // Add the charts to a chosen existing dashboard/report (Phase 3b). Returns
  // true on success, false on failure (the caller keeps the proposal pending).
  // The server-side buildChartSpec is the real gate, but V5GadgetForm's own
  // validation never runs on this path — so re-check before writing. Refusing
  // loudly beats writing a gadget that renders permanently blank.
  const rejectUnsaveable = useCallback(
    (charts: InsightsChartSpec[]): boolean => {
      const bad = charts
        .map(canSaveSpec)
        .find((result): result is { ok: false; reason: string } => !result.ok);
      if (bad) {
        showToast(`Can't save this chart: ${bad.reason}`);
        return true;
      }
      return false;
    },
    [showToast],
  );

  const addToExisting = useCallback(
    async ({
      target,
      charts,
    }: {
      target: AddTarget;
      charts: InsightsChartSpec[];
    }): Promise<boolean> => {
      if (isSaving || charts.length === 0 || rejectUnsaveable(charts)) {
        return false;
      }
      setIsSaving(true);
      try {
        return target.kind === 'dashboard'
          ? await addToExistingDashboard({ dashboard: target.entity, charts })
          : await addToExistingReport({ report: target.entity, charts });
      } catch {
        setIsSaving(false);
        showToast(`Could not add to the ${target.kind}. Please try again.`);
        return false;
      }
    },
    [
      isSaving,
      addToExistingDashboard,
      addToExistingReport,
      rejectUnsaveable,
      showToast,
    ],
  );

  // Build the workspace doc with the content (dashboard/report) added to its All
  // Assets nav item, or — when the workspace has none — a direct nav item in the
  // first non-admin section. Mirrors AddAssetForm's update.
  const withContentAddedToWorkspace = useCallback(
    ({
      workspace,
      item,
      now,
    }: {
      workspace: V3.Workspace;
      item: AddTarget;
      now: string;
    }): V3.Workspace => {
      const { sections } = workspace.navigationSettings;
      const allAssets = sections
        .flatMap((s) => s.items)
        .find(appNavItemTypeCheckers.isAllAssets);

      const withSections = (newSections: V3.Navigation.SideSection[]) => ({
        ...workspace,
        navigationSettings: {
          ...workspace.navigationSettings,
          sections: newSections,
        },
        updatedBy: currentUserId,
        updatedOn: now,
      });

      if (allAssets) {
        const updatedAllAssets: V3.Navigation.AllAssets = {
          ...allAssets,
          dashboards:
            item.kind === 'dashboard'
              ? [
                  ...allAssets.dashboards,
                  { id: item.entity.id, isPinned: false },
                ]
              : allAssets.dashboards,
          reports:
            item.kind === 'report'
              ? [...allAssets.reports, { id: item.entity.id, isPinned: false }]
              : allAssets.reports,
        };
        return withSections(
          sections.map((section) =>
            section.items.some((i) => i.key === allAssets.key)
              ? {
                  ...section,
                  items: section.items.map((i) =>
                    i.key === allAssets.key ? updatedAllAssets : i,
                  ),
                }
              : section,
          ),
        );
      }

      // Fallback: a direct nav item appended to the first non-admin section.
      const navItem: V3.Navigation.Item =
        item.kind === 'dashboard'
          ? {
              key: getIdentifier(),
              type: 'Dashboard',
              label: item.entity.name,
              dashboardId: item.entity.id,
              isAppAdminOnly: false,
            }
          : {
              key: getIdentifier(),
              type: 'Report',
              label: item.entity.name,
              reportId: item.entity.id,
              isAppAdminOnly: false,
            };
      const targetIndex = Math.max(
        sections.findIndex((s) => !s.isAppAdminOnly),
        0,
      );
      return withSections(
        sections.map((section, i) =>
          i === targetIndex
            ? { ...section, items: [...section.items, navItem] }
            : section,
        ),
      );
    },
    [currentUserId],
  );

  // Add the charts to a chosen workspace (Phase 3c): either as a NEW dashboard
  // built from the charts, or by adding an existing dashboard/report the user
  // picked. Returns true on success, false on failure.
  const addToWorkspace = useCallback(
    async ({
      workspace,
      content,
      charts,
    }: {
      workspace: V3.Workspace;
      content: WorkspaceContentChoice;
      charts: InsightsChartSpec[];
    }): Promise<boolean> => {
      if (isSaving) {
        return false;
      }
      if (content.kind === 'new' && charts.length === 0) {
        return false;
      }
      if (rejectUnsaveable(charts)) {
        return false;
      }
      setIsSaving(true);
      try {
        const item: AddTarget =
          content.kind === 'existing'
            ? content.item
            : {
                kind: 'dashboard',
                entity: await persistNewDashboard({
                  title: content.title || 'Insights',
                  charts,
                }),
              };
        const now = DateTime.utc().toISO();
        const updatedWorkspace = withContentAddedToWorkspace({
          workspace,
          item,
          now,
        });
        await STORE.contentDistributions
          .getWorkspacesRef({ accountId: selectedAccountId })
          .doc(updatedWorkspace.id)
          .set(updatedWorkspace);
        return finish({
          kind: 'workspace',
          name: workspace.label,
          route: `/workspaces/${workspace.urlSlug}`,
          message: `Added to workspace “${workspace.label}”`,
        });
      } catch {
        setIsSaving(false);
        showToast('Could not add to the workspace. Please try again.');
        return false;
      }
    },
    [
      isSaving,
      persistNewDashboard,
      withContentAddedToWorkspace,
      selectedAccountId,
      finish,
      rejectUnsaveable,
      showToast,
    ],
  );

  // Commit a create-metric proposal (Phase 3c): persist a Metrics.NormalMetric /
  // CompoundMetric (which bumps the metric cache version). No navigation — the
  // user can immediately chart it from the chat. Returns the new metric (so
  // the agent can be told its id), or undefined when nothing was created.
  const createMetricFromProposal = useCallback(
    async (action: ProposedAction): Promise<CreatedMetric | undefined> => {
      if (
        action.kind !== 'create_metric' &&
        action.kind !== 'create_compound_metric'
      ) {
        return undefined;
      }
      if (isSaving) {
        return undefined;
      }
      setIsSaving(true);
      const now = DateTime.utc().toISO();
      const base = {
        id: getIdentifier(),
        name: action.metric.name,
        description: action.metric.description,
        formatting: action.metric.formatting ?? {},
        status: 'public' as const,
        isAIGenerated: true,
        createdBy: currentUserId,
        createdOn: now,
        updatedBy: currentUserId,
        updatedOn: now,
      };
      try {
        if (action.kind === 'create_metric') {
          const metric: Metrics.NormalMetric = {
            ...base,
            type: 'normal',
            dataType: action.metric.dataset,
            field: action.metric.field,
            // The function validated aggFunc against the allowed set.
            aggFunc: action.metric.aggFunc as Metrics.NormalMetric['aggFunc'],
            filters: {},
          };
          await createMetric(metric, selectedAccountId);
        } else {
          const metric: Metrics.CompoundMetric = {
            ...base,
            type: 'compound',
            metricIds: action.metric.metricIds,
            expression: action.metric.expression,
          };
          await createCompoundMetric(metric, selectedAccountId);
        }
        setIsSaving(false);
        showToast(`Created metric “${action.metric.name}”`);
        trackEvent('Insights - Content Built', { kind: 'metric' });
        return { metricId: base.id, metricName: base.name };
      } catch {
        setIsSaving(false);
        showToast('Could not create the metric. Please try again.');
        return undefined;
      }
    },
    [isSaving, currentUserId, selectedAccountId, showToast, trackEvent],
  );

  // Commit a create-dashboard/report proposal. Returns true on success, false on
  // failure (the caller keeps the proposal pending so the user can retry).
  const createContent = useCallback(
    async (action: ProposedAction): Promise<boolean> => {
      if (action.kind !== 'dashboard' && action.kind !== 'report') {
        return false;
      }
      if (
        isSaving ||
        action.charts.length === 0 ||
        rejectUnsaveable(action.charts)
      ) {
        return false;
      }
      const { title } = action;
      setIsSaving(true);
      try {
        return action.kind === 'dashboard'
          ? await createDashboard({ title, charts: action.charts })
          : await createReport({ title, charts: action.charts });
      } catch {
        setIsSaving(false);
        showToast(`Could not create the ${action.kind}. Please try again.`);
        return false;
      }
    },
    [isSaving, createDashboard, createReport, rejectUnsaveable, showToast],
  );

  return {
    isSaving,
    createContent,
    addToExisting,
    addToWorkspace,
    createMetricFromProposal,
  };
};

export default useCreateInsightsContent;
